import { Injectable, Injector } from '@angular/core';
import {
  MessageStore,
  messageTransferSchema,
  Message,
  MessageDraft,
  MessageTransfer,
  QuotedMessage,
  Reaction,
  ReactionPayload,
  TalkPermissionPayload,
} from '../../state/messages.repository';
// Static import is safe: Angular instantiates ChatService first (it has no
// OdinService constructor dep), then OdinService (which has ChatService).
// `_injector.get(OdinService)` only runs at call time, after both exist.
import { OdinService } from './odin.service';
import { MessageReadStateStore } from '../../state/message-read-state.repository';
import { EmbedResolverService } from './embed-resolver.service';
import { User, UserStore } from '../../state/user.repository';
import { NgWhiteboardService, WhiteboardElement } from '../../shared/components/ng-whiteboard';
import { uint8ArrayToValue } from '@4players/odin';
import { UserService } from './user.service';
import { filter, Subject, take } from 'rxjs';
import { soundChatMessage, soundPoked } from '../../shared/constants/sounds';
import { AudioMuteStates, UserSettingsStore } from '../../state/user-settings.repository';
import { PermissionStore } from '../../state/permissions.repository';
import { TranslateService } from '@ngx-translate/core';
import { DesktopNotificationService } from './desktop-notification.service';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  talkPermissionGranted$ = new Subject<{ sender: User }>();

  constructor(
    private _messageStore: MessageStore,
    private _userService: UserService,
    private _whiteboardService: NgWhiteboardService,
    private _userSettingsStore: UserSettingsStore,
    private _permissionStore: PermissionStore,
    private _usersStore: UserStore,
    private _translateService: TranslateService,
    private _messageReadState: MessageReadStateStore,
    private _embedResolver: EmbedResolverService,
    private _desktopNotifications: DesktopNotificationService,
    // Injector is used to lazily resolve OdinService when sending outbound
    // wire messages from this service. Constructor-injecting OdinService would
    // form a cycle because OdinService already depends on ChatService.
    private _injector: Injector
  ) {
    // Treat any history that was already persisted before this session started
    // as already read. Wait on MessageStore.initialized$ because rehydration is
    // asynchronous (IDB-backed) — reading the store synchronously here would
    // seed before messages are loaded and do nothing.
    this._messageStore.initialized$
      .pipe(
        filter((ready) => ready),
        take(1)
      )
      .subscribe(() => this._seedReadState());

    this._usersStore.raisedHandChanges$.pipe().subscribe((users) => {
      for (const user of users) {
        console.log('raisedHandChanges', user);
        this.addRaiseHandMessage(user);
      }
    });
  }

  private _seedReadState(): void {
    const state = this._messageStore.store.getValue();
    const latestByRoom = new Map<string, number>();
    for (const id of state.ids) {
      const m = state.entities[id as string];
      if (!m) continue;
      const t = m.time.getTime();
      const cur = latestByRoom.get(m.roomId) ?? 0;
      if (t > cur) latestByRoom.set(m.roomId, t);
    }
    for (const [roomId, time] of latestByRoom) {
      this._messageReadState.seedIfMissing(roomId, time);
    }
  }

  parseMessage(data: Uint8Array): MessageTransfer {
    const message = uint8ArrayToValue(data);
    return messageTransferSchema.parse(message);
  }

  async buildMessage(sender: User, messageData: MessageTransfer, target?: User) {
    if (
      messageData.kind === 'message' ||
      messageData.kind === 'poke' ||
      messageData.kind === 'mute'
    ) {
      let receiver: Message['receiver'];
      if (target) {
        receiver = {
          name: target.data.name,
          id: target.id,
          avatar: target.data.avatar,
        };
      }
      const message: MessageDraft = {
        roomId: this._userSettingsStore.value.roomId,
        breakoutRoomId: sender.data.breakoutRoomId ?? '',
        time: new Date(),
        origin: sender.isSelf ? 'self' : 'remote',
        sender: {
          name: sender.data.name,
          id: sender.id,
          avatar: sender.data.avatar,
        },
        receiver,
        ...messageData,
      };

      // clone text for embed generation
      let text = '';
      switch (messageData.kind) {
        case 'message':
          message.payload = messageData.payload;
          if (messageData.payload.text) {
            this.playSound(soundChatMessage);
            text = messageData.payload.text.trim();
            // `payload.text` stays as the raw markdown source the sender
            // typed. Rendering to HTML happens at display time in
            // `ChatMsg` via a memoized computed signal — see
            // `shared/helpers/message-html.ts` for why we moved the
            // pipeline out of the receive path.
          }
          break;
        case 'poke':
          message.payload = messageData.payload
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;');
          if (message.kind === 'poke') {
            if (!sender.isSelf) {
              this.playSound(soundPoked);
              this.buildNotification(
                message,
                this._translateService.instant('[name] poked you:', {
                  name: message.sender.name,
                })
              );
            }
          }
          break;
        case 'mute': {
          message.payload = this._translateService.instant('Your microphone was muted by [name].', {
            name: message.sender.name,
          });
          if (message.kind === 'mute') {
            if (target?.isSelf) {
              this.buildNotification(
                message,
                this._translateService.instant('Microphone muted:'),
                true
              );
              this._userSettingsStore.update({
                mutedState: AudioMuteStates.mic,
              });
            } else {
              message.payload = this._translateService.instant('You muted [name]', {
                name: message.receiver?.name,
              });
            }
          }
          break;
        }
      }
      const stored = this._messageStore.add(message);

      // Fire-and-forget OGP resolution. The resolver handles URL
      // extraction, per-URL caching, request timeout, prune-race guard,
      // and the embed upsert.
      void this._embedResolver.resolveForMessage(stored);
    }

    if (messageData.kind === 'whiteboard' && sender.id !== this._userService.ownUser()?.id) {
      this.addWhiteBoardMessage(messageData.payload, sender.id);
    }

    if (messageData.kind === 'whiteboard-clear') {
      this._whiteboardService.data.next([]);
      this._whiteboardService.foreignData.next({});
    }

    if (messageData.kind === 'talkPermission') {
      const payload = messageData.payload as TalkPermissionPayload;

      if (target?.isSelf) {
        this._userService.updateUserData({ hasRaisedHand: false });

        if (payload.isAccepted) {
          if (!this._permissionStore.value.useMicrophone) {
            this._userSettingsStore.update({ talkPermissionGranted: true });
            this.talkPermissionGranted$.next({ sender });
          }
          this._addTalkPermissionMessage(sender, payload.message, true);
        } else {
          this._addTalkPermissionMessage(sender, payload.message, false);
        }
      } else if (sender.isSelf) {
        this._addTalkPermissionMessage(sender, payload.message, payload.isAccepted, target);
      }
    }

    if (messageData.kind === 'reaction') {
      this._applyReactionFromWire(sender, messageData.payload);
    }

    if (messageData.kind === 'file') {
      // A file shared in the room: rendered as a file card, counted as unread
      // like any user message.
      this._messageStore.add({
        ...messageData,
        roomId: this._userSettingsStore.value.roomId,
        breakoutRoomId: sender.data.breakoutRoomId ?? '',
        time: new Date(),
        origin: sender.isSelf ? 'self' : 'remote',
        sender: {
          name: sender.data.name,
          id: sender.id,
          avatar: sender.data.avatar,
        },
      });
      if (!sender.isSelf) this.playSound(soundChatMessage);
    }
  }

  addWhiteBoardMessage(value: WhiteboardElement[], from: string | undefined) {
    const foreignData = this._whiteboardService.foreignData.value;
    const key = from || 'undefined';
    if (!foreignData[key]) {
      foreignData[key] = {
        visible: true,
        data: [],
      };
    }
    if (value.length === 0) {
      delete foreignData[key];
    } else {
      foreignData[key].data = value;
    }
    this._whiteboardService.foreignData.next(foreignData);
  }

  playSound(sound: HTMLAudioElement) {
    if (
      !this._userSettingsStore.value.playChatSounds ||
      !this._userSettingsStore.value.playAppSounds
    ) {
      return;
    }
    sound.play().catch();
  }

  private _addTalkPermissionMessage(
    sender: User,
    text: string,
    isAccepted: boolean,
    target?: User
  ) {
    let displayText: string;
    if (sender.isSelf && target) {
      displayText = isAccepted
        ? this._translateService.instant('You granted talk permission to [name].', {
            name: target.data.name,
          })
        : this._translateService.instant('You denied talk permission to [name].', {
            name: target.data.name,
          });
    } else {
      displayText = isAccepted
        ? this._translateService.instant('[name] granted you talk permission.', {
            name: sender.data.name,
          })
        : this._translateService.instant('[name] denied your talk request.', {
            name: sender.data.name,
          });
    }
    this._messageStore.add({
      time: new Date(),
      origin: sender.isSelf ? 'self' : 'remote',
      sender: { name: sender.data.name, id: sender.id, avatar: sender.data.avatar },
      roomId: this._userSettingsStore.value.roomId,
      breakoutRoomId: sender.data.breakoutRoomId ?? '',
      scope: 'system',
      kind: 'message',
      payload: { text: displayText },
    });
  }

  addRaiseHandMessage(user: User) {
    this._messageStore.add({
      roomId: this._userSettingsStore.value.roomId,
      breakoutRoomId: user.data.breakoutRoomId ?? '',
      time: new Date(),
      scope: 'room',
      origin: user.isSelf ? 'self' : 'remote',
      sender: { name: user.data.name, id: user.id, avatar: user.data.avatar },
      kind: 'message',
      payload: {
        text: `✋ ${this._translateService.instant('[name] has raised a hand.', { name: user.data.name })}`,
      },
    });
  }

  // -- Reactions ----------------------------------------------------------
  //
  // See ../../../../docs/agent-reference/chat.md#pattern-target-mutating-message-kinds.
  // Wire-only `kind: 'reaction'` messages mutate the target
  // message's `reactions[]` field; the wire messages themselves are never
  // stored as their own rows.

  // Builds the next reactions array for a target, given a user action. Pure
  // — does not touch the store. Returns `null` when the action is a no-op
  // (adding an already-present `(userId, emoji)` pair, or removing one that
  // doesn't exist) so callers can skip the upsert.
  private _computeNextReactions(
    current: Reaction[] | undefined,
    userId: string,
    emoji: string,
    action: 'add' | 'remove',
    time: Date
  ): Reaction[] | null {
    const existing = current ?? [];
    const has = existing.some((r) => r.userId === userId && r.emoji === emoji);
    if (action === 'add') {
      if (has) return null;
      return [...existing, { userId, emoji, time }];
    }
    // remove
    if (!has) return null;
    return existing.filter((r) => !(r.userId === userId && r.emoji === emoji));
  }

  // Handler used by both the wire-receipt path (in `buildMessage`) and the
  // local-only outbound path (when the user reacts inside a solo breakout).
  // Dropped silently when the target isn't in the store — that case covers
  // both pruned history and ODIN cross-sender ordering where a reaction
  // arrives before the message it references.
  private _applyReactionFromWire(sender: User, payload: ReactionPayload): void {
    const state = this._messageStore.store.getValue();
    const target = state.entities[payload.targetMessageId];
    if (!target) return;
    if (target.origin === 'system') return;

    const userId = sender.id;
    if (!userId) return;
    const next = this._computeNextReactions(
      target.reactions,
      userId,
      payload.emoji,
      payload.action,
      new Date()
    );
    if (!next) return;
    this._messageStore.upsert({ ...target, reactions: next });
  }

  // Toggles the own user's reaction with `emoji` on `target`. Mints a wire
  // message and either broadcasts it (via OdinService, which echoes it back
  // through `buildMessage` and produces the local apply) or — when there are
  // no peers to deliver to (solo breakout, missing DM counterparty) — applies
  // locally only.
  async sendReaction(target: Message, emoji: string): Promise<void> {
    const ownUser = this._userService.ownUser();
    if (!ownUser) return;
    if (target.origin === 'system') return;
    const userId = ownUser.id;
    if (!userId) return;

    const alreadyReacted = (target.reactions ?? []).some(
      (r) => r.userId === userId && r.emoji === emoji
    );
    const action: 'add' | 'remove' = alreadyReacted ? 'remove' : 'add';

    const transfer: MessageTransfer = {
      id: crypto.randomUUID(),
      kind: 'reaction',
      scope: target.scope,
      payload: { targetMessageId: target.id, emoji, action },
    };

    const recipients = this.recipientsForTarget(target);
    if (recipients === null) {
      // No peers to notify — apply on our own store so the chip flips, then
      // we're done. Matches the existing "solo breakout" pattern in sendMsg.
      await this.buildMessage(ownUser, transfer);
      return;
    }
    // OdinService.sendMessage echoes through buildMessage on success, so the
    // local apply happens automatically.
    const odinService = this._injector.get(OdinService);
    await odinService.sendMessage(transfer, recipients);
  }

  // Reactions follow the target message's audience, not the sender's current
  // context. DM → just the counterparty; breakout → peers in that breakout;
  // main-room broadcast → empty array (odin treats `[]` as broadcast). Returns
  // `null` to signal "no one to send to — apply locally only".
  recipientsForTarget(target: Message): User[] | null {
    if (target.origin === 'system') return null;

    const all = Object.values(this._usersStore.store.getValue().entities) as (User | undefined)[];

    if (target.scope === 'private' && target.receiver) {
      const otherUserId = target.origin === 'self' ? target.receiver.id : target.sender.id;
      const other = all.find((u): u is User => !!u && !u.isSelf && u.id === otherUserId);
      return other ? [other] : null;
    }

    if (target.breakoutRoomId) {
      const peers = all.filter(
        (u): u is User => !!u && !u.isSelf && u.data.breakoutRoomId === target.breakoutRoomId
      );
      return peers.length > 0 ? peers : null;
    }

    return [];
  }

  // -- Quotes / replies ---------------------------------------------------

  // Builds the self-contained reply preview that travels with the new
  // message. Image-only originals fall back to alt-text (currently absent on
  // the schema; punted to literal `[Image]` until alt is added). The id +
  // senderName + snippet are independently useful: if the recipient has the
  // original locally, click-on-preview can scroll to it; if not, the preview
  // still renders the snippet.
  buildQuotedMessage(target: Message): QuotedMessage {
    return {
      id: target.id,
      senderName: target.sender.name,
      textSnippet: this._snippetFromMessage(target),
    };
  }

  private _snippetFromMessage(message: Message): string {
    const MAX = 200;
    if (message.kind === 'message') {
      // payload.text may include HTML from markdown rendering — strip tags so
      // the snippet is plain text. Cheap regex strip; the source is our own
      // sanitized output, not arbitrary HTML.
      const raw = message.payload.text ?? '';
      const stripped = raw.replace(/<[^>]+>/g, '').trim();
      if (stripped) {
        return stripped.length > MAX ? stripped.slice(0, MAX - 1) + '…' : stripped;
      }
      if (message.payload.attachment?.type === 'image') {
        return this._translateService.instant('[Image]');
      }
    }
    if (message.kind === 'poke' || message.kind === 'mute') {
      const raw = typeof message.payload === 'string' ? message.payload : '';
      return raw.length > MAX ? raw.slice(0, MAX - 1) + '…' : raw;
    }
    return '';
  }

  buildNotification(
    message: Extract<MessageDraft, { kind: 'poke' | 'mute' }>,
    title: string,
    force = false
  ) {
    this._desktopNotifications.notify(title, { body: message.payload, force });
  }
}
